/**
 * El cliente firma con el dedo que recibió el trabajo.
 *
 * ── Tres cosas que hay que hacer bien o no sirve ──
 *
 * 1. **El lienzo se dibuja al tamaño REAL de la pantalla, no al de CSS.** Un celular tiene
 *    2 o 3 píxeles de verdad por cada uno de CSS (`devicePixelRatio`). Si el lienzo se crea
 *    con las medidas de CSS, la firma sale borrosa y con escalones — y una firma borrosa no
 *    respalda nada.
 * 2. **`touchAction: 'none'`.** Sin eso, arrastrar el dedo mueve la página en vez de pintar:
 *    el cliente intenta firmar y lo que hace es bajar la pantalla.
 * 3. **La firma se recorta a lo que se pintó.** Guardar el lienzo entero deja una franja
 *    blanca enorme alrededor de un garabato pequeño, que después se ve como un renglón vacío.
 *
 * Se guarda en PNG y no en JPEG: son trazos sobre blanco, y el JPEG los deja con una aureola
 * gris. Un PNG de una firma pesa 5 a 20 KB, menos que la foto más liviana.
 *
 * ── Por qué se guardan los TRAZOS y no solo lo pintado ──
 *
 * Cazado probando la app el 29-sep-2026: el lienzo quedaba EN BLANCO y el botón de guardar
 * seguía encendido. Un lienzo se borra entero cada vez que se le cambia el tamaño, y aquí se
 * le cambiaba en cada `resize`.
 *
 * En un celular eso no es un caso raro: es lo que pasa SIEMPRE. El cliente firma, toca el
 * campo del nombre, se abre el teclado, la ventana se encoge — y la firma desaparece. Como
 * el botón no dependía de lo pintado sino de «alguien tocó el lienzo», se habría guardado
 * una firma en blanco con el nombre del cliente debajo. Un respaldo que dice que alguien
 * firmó y no tiene firma es peor que no tener respaldo.
 *
 * Por eso ahora se guardan los puntos y, cuando el lienzo cambia de tamaño, se vuelven a
 * pintar. Lo que estaba firmado sigue estando.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { COLOR, s } from './estilos';

export default function Firma({ onGuardar, guardando }) {
  const lienzoRef = useRef(null);
  const pintandoRef = useRef(false);
  /* Los trazos: una lista de líneas, y cada línea una lista de puntos. Van en una referencia
     y no en el estado porque crecen en cada movimiento del dedo, y volver a dibujar la
     pantalla sesenta veces por segundo haría que la línea se entrecorte. */
  const trazosRef = useRef([]);
  const [hayTrazo, setHayTrazo] = useState(false);
  const [nombre, setNombre] = useState('');

  /**
   * Deja el lienzo listo y vuelve a pintar lo que ya estaba firmado.
   *
   * Tocar `width` o `height` de un lienzo lo borra entero — no hay forma de evitarlo—, así
   * que después de ajustarlo hay que repintar. Es lo que hace que abrir el teclado no se
   * lleve la firma por delante.
   */
  const preparar = useCallback(() => {
    const lienzo = lienzoRef.current;
    if (!lienzo) return;
    const caja = lienzo.getBoundingClientRect();
    const escala = window.devicePixelRatio || 1;
    lienzo.width = Math.round(caja.width * escala);
    lienzo.height = Math.round(caja.height * escala);
    const pincel = lienzo.getContext('2d');
    pincel.scale(escala, escala);
    pincel.fillStyle = '#FFFFFF';
    pincel.fillRect(0, 0, caja.width, caja.height);
    pincel.lineWidth = 2.5;
    pincel.lineCap = 'round';
    pincel.lineJoin = 'round';
    pincel.strokeStyle = '#111827';

    for (const trazo of trazosRef.current) {
      if (!trazo.length) continue;
      pincel.beginPath();
      pincel.moveTo(trazo[0].x, trazo[0].y);
      /* Un toque suelto (un punto sin arrastre) se pinta como punto: si solo se unieran
         puntos, un cliente que firma con puntos no dejaría nada. */
      if (trazo.length === 1) pincel.lineTo(trazo[0].x + 0.1, trazo[0].y);
      else for (const p of trazo.slice(1)) pincel.lineTo(p.x, p.y);
      pincel.stroke();
    }
  }, []);

  useEffect(() => {
    preparar();
    window.addEventListener('resize', preparar);
    /* En el celular, abrir el teclado muchas veces NO dispara `resize` sino que encoge la
       ventana visual. Se escuchan las dos cosas: la que falte deja la firma en blanco. */
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', preparar);
    return () => {
      window.removeEventListener('resize', preparar);
      if (vv) vv.removeEventListener('resize', preparar);
    };
  }, [preparar]);

  const donde = (e) => {
    const caja = lienzoRef.current.getBoundingClientRect();
    const punto = e.touches ? e.touches[0] : e;
    return { x: punto.clientX - caja.left, y: punto.clientY - caja.top };
  };

  const empezar = (e) => {
    e.preventDefault();
    pintandoRef.current = true;
    const p = donde(e);
    trazosRef.current.push([p]);
    const pincel = lienzoRef.current.getContext('2d');
    pincel.beginPath();
    pincel.moveTo(p.x, p.y);
    if (!hayTrazo) setHayTrazo(true);
  };

  const mover = (e) => {
    if (!pintandoRef.current) return;
    e.preventDefault();
    const p = donde(e);
    const trazo = trazosRef.current[trazosRef.current.length - 1];
    if (trazo) trazo.push(p);
    const pincel = lienzoRef.current.getContext('2d');
    pincel.lineTo(p.x, p.y);
    pincel.stroke();
  };

  const soltar = () => {
    if (!pintandoRef.current) return;
    pintandoRef.current = false;
    /* Un toque sin arrastre deja un trazo de un solo punto que el navegador no dibuja. Se
       repinta todo para que ese punto aparezca, y así lo que se ve es exactamente lo que se
       va a guardar. */
    const trazo = trazosRef.current[trazosRef.current.length - 1];
    if (trazo && trazo.length === 1) preparar();
  };

  const borrar = () => {
    trazosRef.current = [];
    setHayTrazo(false);
    preparar();
  };

  /** Los bordes de todo lo pintado, para recortar. Se calculan de los trazos guardados. */
  const bordes = () => {
    let b = null;
    for (const trazo of trazosRef.current) {
      for (const p of trazo) {
        b = b ? {
          x1: Math.min(b.x1, p.x), y1: Math.min(b.y1, p.y),
          x2: Math.max(b.x2, p.x), y2: Math.max(b.y2, p.y),
        } : { x1: p.x, y1: p.y, x2: p.x, y2: p.y };
      }
    }
    return b;
  };

  /**
   * Recorta a lo pintado, con un margen, y devuelve el PNG.
   *
   * El margen evita que la firma quede pegada al borde. Y si lo pintado es más chico que el
   * margen (un punto suelto), igual sale una imagen con sentido.
   */
  const recortar = () => {
    const lienzo = lienzoRef.current;
    const b = bordes();
    const escala = window.devicePixelRatio || 1;
    if (!b) return lienzo.toDataURL('image/png');

    const margen = 10;
    const x = Math.max(0, (b.x1 - margen)) * escala;
    const y = Math.max(0, (b.y1 - margen)) * escala;
    const ancho = Math.min(lienzo.width - x, (b.x2 - b.x1 + margen * 2) * escala);
    const alto = Math.min(lienzo.height - y, (b.y2 - b.y1 + margen * 2) * escala);

    const recorte = document.createElement('canvas');
    recorte.width = Math.max(1, Math.round(ancho));
    recorte.height = Math.max(1, Math.round(alto));
    const pincel = recorte.getContext('2d');
    pincel.fillStyle = '#FFFFFF';
    pincel.fillRect(0, 0, recorte.width, recorte.height);
    pincel.drawImage(lienzo, x, y, ancho, alto, 0, 0, recorte.width, recorte.height);
    return recorte.toDataURL('image/png');
  };

  const listo = hayTrazo && nombre.trim().length >= 3;

  return (
    <div style={{ display: 'grid', gap: '10px', minWidth: 0 }}>
      <div style={{ ...s.sub, marginBottom: '-2px' }}>
        Que firme quien recibe el trabajo, con el dedo:
      </div>
      <canvas
        ref={lienzoRef}
        style={{
          width: '100%', height: '180px', background: '#FFFFFF',
          borderRadius: '12px', border: `1px solid ${COLOR.borde}`,
          touchAction: 'none', display: 'block',
        }}
        onMouseDown={empezar} onMouseMove={mover} onMouseUp={soltar} onMouseLeave={soltar}
        onTouchStart={empezar} onTouchMove={mover} onTouchEnd={soltar}
      />
      <input
        style={s.campo}
        placeholder="Nombre de quien firma"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        autoComplete="off"
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '10px' }}>
        <button style={s.botonSuave} onClick={borrar} disabled={guardando}>↺ Borrar</button>
        <button
          style={{
            ...s.boton,
            background: listo ? COLOR.verde : 'rgba(255,255,255,0.06)',
            color: listo ? '#052e21' : COLOR.suave,
            cursor: listo && !guardando ? 'pointer' : 'not-allowed',
          }}
          disabled={!listo || guardando}
          onClick={() => onGuardar({ firma_base64: recortar(), firma_nombre: nombre.trim() })}
        >
          {guardando ? 'Guardando…' : '✔ Guardar firma'}
        </button>
      </div>
      {/* Se dice ANTES de firmar, no después: el cliente ya se fue cuando uno se da cuenta. */}
      <div style={{ ...s.sub, fontSize: '12px' }}>
        Una vez guardada no se puede cambiar desde aquí.
      </div>
    </div>
  );
}
